(() => {
  if (!customElements.get('combo-variant-picker')) {
    class ComboVariantPicker extends HTMLElement {
      connectedCallback() {
        this.groups = Array.from(this.querySelectorAll('[data-combo-group]'));
        this.messageEl = this.querySelector('[data-combo-unavailable-message]');
        this.topLabel = this.dataset.topLabel || 'Top';
        this.bottomLabel = this.dataset.bottomLabel || 'Bottom';
        this.selectionState = { valid: false };

        this.groups.forEach((group) => {
          try {
            group.variants = JSON.parse(group.querySelector('script[data-combo-variants]')?.textContent || '[]');
          } catch {
            group.variants = [];
          }
          group.addEventListener('change', () => this.handleChange());
        });
        this.handleChange();
      }

      getSelectedOptionValues(group) {
        const values = [];
        group.querySelectorAll('input[type="radio"]:checked, select').forEach((input) => {
          const position = Number(input.dataset.optionPosition || input.closest('select')?.dataset.optionPosition);
          values[position - 1] = input.value;
        });
        return values;
      }

      findMatchingVariant(group) {
        const selected = this.getSelectedOptionValues(group);
        if (!selected.length || selected.some((value) => value === undefined)) return null;
        return group.variants.find((variant) => {
          const values = [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
          return values.length === selected.length && values.every((value, index) => value === selected[index]);
        });
      }

      handleChange() {
        let valid = true;
        let outOfStockLabel = null;
        const sizes = {};
        const ids = {};
        this.groups.forEach((group) => {
          const variant = this.findMatchingVariant(group);
          const key = group.dataset.comboGroup;
          const label = key === 'top' ? this.topLabel : this.bottomLabel;
          if (!variant) {
            valid = false;
            return;
          }
          sizes[key] = [variant.option1, variant.option2, variant.option3].filter(Boolean).join(' / ');
          ids[key] = variant.id;
          if (!variant.available) {
            valid = false;
            outOfStockLabel = label;
          }
        });

        if (this.messageEl) {
          this.messageEl.hidden = valid;
          if (!valid) this.messageEl.textContent = outOfStockLabel
            ? `Selected ${outOfStockLabel} size is out of stock.`
            : 'Please select a size for both Top and Bottom.';
        }
        this.selectionState = {
          valid,
          topLabel: this.topLabel,
          bottomLabel: this.bottomLabel,
          topSize: sizes.top || null,
          bottomSize: sizes.bottom || null,
          topVariantId: ids.top || null,
          bottomVariantId: ids.bottom || null,
        };
        this.dispatchEvent(new CustomEvent('combo-variant:change', { bubbles: true, detail: this.selectionState }));
      }
    }
    customElements.define('combo-variant-picker', ComboVariantPicker);
  }

  if (!customElements.get('combo-buy-button')) {
    class ComboBuyButton extends HTMLElement {
      connectedCallback() {
        this.variantId = this.dataset.variantId;
        this.submitButton = this.querySelector('[data-combo-buy-submit]');
        this.label = this.querySelector('[data-combo-buy-label]');
        this.errorEl = this.querySelector('[data-combo-buy-error]');
        this.qtyInput = this.querySelector('[data-combo-qty-input]');
        this.state = { valid: false };
        this.querySelector('[data-combo-qty-decrease]')?.addEventListener('click', () => { this.qtyInput.value = Math.max(1, Number(this.qtyInput.value) - 1); });
        this.querySelector('[data-combo-qty-increase]')?.addEventListener('click', () => { this.qtyInput.value = Number(this.qtyInput.value) + 1; });
        this.submitButton?.addEventListener('click', () => this.handleAdd());

        const picker = this.getPicker();
        if (picker?.selectionState) this.updateFromState(picker.selectionState);
        document.addEventListener('combo-variant:change', (event) => {
          const sourcePicker = event.target;
          if (sourcePicker === this.getPicker()) this.updateFromState(event.detail);
        });
      }

      getPicker() {
        return this.closest('.quick-add-modal__content, .product-details, product-information')?.querySelector('combo-variant-picker')
          || document.querySelector('combo-variant-picker');
      }

      updateFromState(state) {
        this.state = state || { valid: false };
        if (this.submitButton) this.submitButton.disabled = !this.state.valid;
        if (this.errorEl) this.errorEl.hidden = true;
      }

      async handleAdd() {
        if (!this.state.valid || !this.variantId) return;
        const quantity = Math.max(1, Number(this.qtyInput?.value) || 1);
        const properties = {};
        if (this.state.topLabel && this.state.topSize) properties[`${this.state.topLabel} Size`] = this.state.topSize;
        if (this.state.bottomLabel && this.state.bottomSize) properties[`${this.state.bottomLabel} Size`] = this.state.bottomSize;
        if (this.state.topVariantId) properties['_Top Variant ID'] = String(this.state.topVariantId);
        if (this.state.bottomVariantId) properties['_Bottom Variant ID'] = String(this.state.bottomVariantId);
        const sections = Array.from(document.querySelectorAll('cart-items-component')).map((el) => el.dataset.sectionId).filter(Boolean);
        const payload = { items: [{ id: Number(this.variantId), quantity, properties }] };
        if (sections.length) payload.sections = sections.join(',');

        const originalText = this.label?.textContent;
        this.submitButton.disabled = true;
        if (this.label) this.label.textContent = 'Adding...';
        try {
          const response = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart.');
          this.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: data, sourceId: String(this.variantId), data: { source: 'combo-buy-button', itemCount: quantity, productId: this.dataset.productId, sections: data.sections, didError: false } } }));
          if (this.label) this.label.textContent = 'Added!';
          setTimeout(() => { if (this.label) this.label.textContent = originalText; }, 1500);
        } catch (error) {
          if (this.errorEl) { this.errorEl.textContent = error.message || 'Something went wrong. Please try again.'; this.errorEl.hidden = false; }
          if (this.label) this.label.textContent = originalText;
        } finally {
          this.submitButton.disabled = !this.state.valid;
        }
      }
    }
    customElements.define('combo-buy-button', ComboBuyButton);
  }
})();
